import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v1Api } from '../../services/api';
import { WeeklyRosterData, StartSitAnalysis } from '../../types';
import Card from '../../components/common/Card';
import Skeleton from '../../components/common/Skeleton';
import WeeklyRosterGrid from '../../components/roster/WeeklyRosterGrid';
import StartSitImpactPanel from '../../components/roster/StartSitImpactPanel';

const RosterWeekView: React.FC = () => {
  const { teamId, weekNum, espnLeagueId } = useParams<{ teamId: string; weekNum: string; espnLeagueId: string }>();
  const navigate = useNavigate();
  const goToPlayer = (playerId: number) =>
    navigate(`/leagues/espn/${espnLeagueId}/players/${playerId}`);
  // const context = useContext(TeamContext) as TeamContextData;
  
  const [weeklyRoster, setWeeklyRoster] = useState<WeeklyRosterData | null>(null);
  const [startSitAnalysis, setStartSitAnalysis] = useState<StartSitAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isByeWeek, setIsByeWeek] = useState(false);

  const selectedWeek = weekNum ? parseInt(weekNum) : 14;
  const [rosterSortBy, setRosterSortBy] = useState<'name' | 'points' | 'position' | 'status'>('status');

  useEffect(() => {
    const loadWeekData = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsByeWeek(false);
        
        const [weeklyRosterData, startSitData] = await Promise.all([
          v1Api.teams.getWeeklyRoster(parseInt(teamId!), selectedWeek),
          v1Api.teams.getStartSitAnalysis(parseInt(teamId!), selectedWeek)
        ]);

        setWeeklyRoster(weeklyRosterData);
        setStartSitAnalysis(startSitData);
      } catch (err: any) {
        console.error('Error loading week data:', err);
        
        // Check if this is a 404 error indicating no roster data (bye week)
        const isNoDataError = err?.response?.status === 404 && 
                              err?.response?.data?.detail?.includes('No roster data found');
        
        if (isNoDataError) {
          setIsByeWeek(true);
          setError(null);
        } else {
          setError('Failed to load roster data. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (teamId && weekNum) {
      loadWeekData();
    }
  }, [teamId, selectedWeek, weekNum]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Skeleton className="h-96 w-full" />
          </div>
          <div>
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="text-accent-danger mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-accent-primary text-background-primary rounded font-medium hover:brightness-110 transition-all active:translate-y-px"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  if (isByeWeek) {
    return (
      <Card className="p-8">
        <div className="text-center">
          <div className="text-6xl mb-4">🏖️</div>
          <h3 className="text-xl font-semibold text-slate-100 mb-2">
            Bye Week - Week {selectedWeek}
          </h3>
          <p className="text-slate-400">
            No roster data available for this week. The team was on bye or this week hasn't occurred yet.
          </p>
        </div>
      </Card>
    );
  }

  if (!weeklyRoster) {
    return (
      <Card className="p-6">
        <div className="text-center text-slate-400">
          No roster data available for Week {selectedWeek}.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Week Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-slate-100">
          Week {selectedWeek} Roster
        </h3>
        {weeklyRoster.matchup && (
          <div className="text-slate-400">
            vs {weeklyRoster.matchup.opponent_name} • 
            Score: {weeklyRoster.matchup.team_score.toFixed(1)} - {weeklyRoster.matchup.opponent_score.toFixed(1)}
            {weeklyRoster.matchup.result && (
              <span className={`ml-2 font-medium ${
                weeklyRoster.matchup.result === 'win' ? 'text-green-400' : 'text-red-400'
              }`}>
                {weeklyRoster.matchup.result.toUpperCase()}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Roster Grid */}
        <div className="lg:col-span-2">
          <WeeklyRosterGrid
            rosterData={weeklyRoster}
            sortBy={rosterSortBy}
            onSortChange={setRosterSortBy}
            onPlayerClick={goToPlayer}
          />
        </div>

        {/* Start/Sit Analysis Panel */}
        <div>
          {startSitAnalysis && (
            <StartSitImpactPanel
              analysis={startSitAnalysis}
              onPlayerClick={goToPlayer}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RosterWeekView;